import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { getUserActivePartner } from '@core/models';
import { AuthStore } from '../store/auth.store';
import { UserStore } from '../store/user.store';

/**
 * Gates a route on a permission feature (e.g. `view_partnerships`). Waits until auth is
 * resolved and both the permissions and the user are loaded, then derives the user's enabled
 * features for their active partner (Xemex super-admin permissions live under {@link XEMEX_OID}).
 * Allows when the feature is present, otherwise redirects to the default page.
 *
 * On a direct deep link the shell (`PagesComponent`) hasn't loaded the user yet — guards run
 * before it activates — so the guard kicks off the user fetch itself to avoid waiting forever.
 * Permissions load independently (AuthStore, on authentication), so they need no priming here.
 */
export function featureGuard(feature: string): CanActivateFn {
  return () => {
    const authStore = inject(AuthStore);
    const userStore = inject(UserStore);
    const router = inject(Router);

    // Created in the guard's injection context; consumed below.
    const authReady$ = toObservable(authStore.authReady);
    const permissions$ = toObservable(authStore.permissions);
    const user$ = toObservable(userStore.user);

    return authReady$.pipe(
      filter(ready => ready),
      take(1),
      tap(() => {
        // Ensure the user is loaded even on a deep link (PagesComponent may not be active yet).
        if (!userStore.user().object_id && authStore.oid()) {
          userStore.getUserMethod(authStore.oid());
        }
      }),
      switchMap(() =>
        combineLatest([permissions$, user$]).pipe(
          filter(([permissions, user]) => permissions !== undefined && !!user.object_id),
          take(1),
          map(checkFeaturePermissions(feature, router)),
        ),
      ),
    );
  };
}
function checkFeaturePermissions(feature: string, router: Router): (value: [import("./models").PermissionModel[] | undefined, import("@core/models").UserProfile], index: number) => true | import("@angular/router").UrlTree {
  return ([permissions, user]) => {
    const partnerOid = getUserActivePartner(user)?.partnerOid;
    const features = permissions
      ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
      .flatMap(p => p.roles)
      .flatMap(r => r.features)
      .flatMap(f => f.features)
      .filter(f => f.featureEnabled)
      .map(f => f.featureName) ?? [];
    return features.includes(feature)
      ? true
      : router.createUrlTree(['/', APP_PATHS.Devices]);
  };
}

